import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

// One WebSocket connection to the BLOBL Core. Components subscribe with useRealtime(event, handler).
// After a reconnect a synthetic 'resync' event fires, because events may have been missed meanwhile.

const IDLE_AFTER_MS = 10 * 60 * 1000;
const TYPING_TIMEOUT_MS = 8000;

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const { user, refreshCounts, refreshUser, updateUser } = useAuth();
  const socketRef = useRef(null);
  const listenersRef = useRef(new Map());
  const [connected, setConnected] = useState(false);
  const [presence, setPresence] = useState({});
  const [typing, setTyping] = useState({});
  const typingTimers = useRef({});

  const dispatch = useCallback((event, data) => {
    listenersRef.current.get(event)?.forEach(handler => handler(data));
  }, []);

  // Keyed by "<conversationId>:<userId>"
  const setTypingFor = useCallback((conversationId, userId, isTyping) => {
    const key = `${conversationId}:${userId}`;
    clearTimeout(typingTimers.current[key]);
    setTyping(prev => ({ ...prev, [key]: isTyping }));
    if (isTyping) {
      typingTimers.current[key] = setTimeout(() => setTyping(prev => ({ ...prev, [key]: false })), TYPING_TIMEOUT_MS);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    let everConnected = false;
    const socket = io({
      path: '/socket.io',
      auth: (cb) => cb({ token: localStorage.getItem('token') }),
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      if (everConnected) dispatch('resync');
      everConnected = true;
      refreshCounts();
    });
    socket.on('disconnect', () => setConnected(false));
    // Session expired or revoked: /me returns 401 and AuthContext signs out
    socket.on('connect_error', (err) => { if (err.message === 'unauthorized') refreshUser(); });

    socket.on('presence:snapshot', (snapshot) => setPresence(snapshot || {}));
    socket.on('presence:update', ({ userId, status }) => setPresence(prev => ({ ...prev, [userId]: status })));
    socket.on('presence:self', ({ status }) => updateUser({ status_preference: status }));
    socket.on('dm:typing', ({ conversation_id, from, typing: isTyping }) => setTypingFor(conversation_id, from, isTyping));
    socket.on('dm:message', (m) => setTypingFor(m.conversation_id, m.sender_id, false));

    for (const ev of ['dm:message', 'dm:read', 'dm:request', 'dm:deleted', 'dm:conversation_deleted', 'dm:conversation_updated', 'dm:block']) {
      socket.on(ev, () => refreshCounts());
    }
    socket.onAny((event, data) => dispatch(event, data));

    // Auto "away" after 10 minutes without activity
    let idle = false;
    let idleTimer;
    const markActive = () => {
      clearTimeout(idleTimer);
      if (idle) { idle = false; socket.emit('presence:idle', { idle: false }); }
      idleTimer = setTimeout(() => { idle = true; socket.emit('presence:idle', { idle: true }); }, IDLE_AFTER_MS);
    };
    const activityEvents = ['mousemove', 'keydown', 'touchstart', 'focus'];
    activityEvents.forEach(ev => window.addEventListener(ev, markActive, { passive: true }));
    markActive();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach(ev => window.removeEventListener(ev, markActive));
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
      setPresence({});
      setTyping({});
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const on = useCallback((event, handler) => {
    const map = listenersRef.current;
    if (!map.has(event)) map.set(event, new Set());
    map.get(event).add(handler);
    return () => map.get(event)?.delete(handler);
  }, []);

  const emit = useCallback((event, data) => socketRef.current?.emit(event, data), []);

  const setStatus = useCallback((status) => emit('presence:set', { status }), [emit]);

  return (
    <RealtimeContext.Provider value={{ connected, presence, typing, on, emit, setStatus }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export const useRealtimeContext = () => useContext(RealtimeContext);

// Subscribe to a realtime event for the lifetime of the component
export function useRealtime(event, handler) {
  const { on } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  useEffect(() => on(event, (data) => handlerRef.current(data)), [on, event]);
}

// Subscribe one handler to several events
export function useRealtimeEvents(events, handler) {
  const { on } = useContext(RealtimeContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const key = events.join('|');
  useEffect(() => {
    const offs = key.split('|').map(ev => on(ev, (data) => handlerRef.current(data, ev)));
    return () => offs.forEach(off => off());
  }, [on, key]);
}
